import { getHost, getLocalToken, getAuthToken } from "../../../redux/api/config";

// Streams the response of POST /admin/temp-products/bulk-stage as NDJSON.
//
// We can't go through axiosInstance because axios buffers the entire response
// before resolving — defeats the whole point of NDJSON streaming. Instead we
// hit fetch() directly, attach the same Authorization: Bearer token that
// axiosInstance attaches, and read the body via getReader() + TextDecoder.
//
// Three event types come through the stream, all NDJSON (one JSON object per
// line, trailing newline per event):
//   { event: "started", total }                                — once, first
//   { event: "row", row, name, barcode,
//     destination: "live"|"staged"|"rejected",
//     status: "ok"|"failed",                                  — preserved alias
//     product_id?,   // when destination = "live"
//     temp_id?,      // when destination = "staged"
//     errors? }      // when destination = "rejected"         — one per input row
//   { event: "summary", total, live, staged, rejected, failed, by_code }
//                                                             — once, last
// `row` is 0-based. `failed` on the summary is an alias of `rejected` and is
// emitted for back-compat — readers should prefer `rejected`.
//
// HTTP-level rejections (400 from the request envelope validators) come back
// with a JSON body { status: false, error: { code, params, message } } and we
// throw `BulkStageRequestError` so the caller can render the request-level
// error card without going through the per-row UI.

export class BulkStageRequestError extends Error {
  constructor(httpStatus, errorBody) {
    super(errorBody?.message || `Request rejected with status ${httpStatus}`);
    this.name = "BulkStageRequestError";
    this.httpStatus = httpStatus;
    this.code = errorBody?.code || "ERR_UNKNOWN";
    this.params = errorBody?.params || {};
  }
}

// Thrown when no events arrive from the server within `inactivityMs`. Common
// cause is a backend exception mid-stream that left the connection dangling
// (headers already sent, can't be turned into a proper 500). The caller should
// treat this as "we don't know what landed — check the staging list".
export class BulkStageStreamTimeoutError extends Error {
  constructor(inactivityMs) {
    super(`No events received from server for ${Math.round(inactivityMs / 1000)}s.`);
    this.name = "BulkStageStreamTimeoutError";
    this.inactivityMs = inactivityMs;
  }
}

const DEFAULT_INACTIVITY_MS = 60_000;

const BULK_STAGE_PATH = "admin/temp-products/bulk-stage";

function buildUrl() {
  const host = getHost() || "";
  // getHost() may or may not have a trailing slash; normalize.
  return host.replace(/\/$/, "") + "/" + BULK_STAGE_PATH;
}

function buildHeaders() {
  const token = getLocalToken();
  const authToken = getAuthToken();
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/x-ndjson",
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  // The legacy "auth-token" header is harmless to include; some routes still
  // read it, and the bulk-stage backend ignores it.
  if (authToken) headers["auth-token"] = authToken;
  return headers;
}

// `onEvent` is called once per parsed NDJSON event. `signal` is an
// AbortSignal — when aborted, the underlying fetch is cancelled and the
// stream loop bails out via an AbortError that the caller can catch.
//
// `inactivityMs` is a safety net: if no events arrive for this duration we
// abort the fetch ourselves and throw `BulkStageStreamTimeoutError`. Default
// 60s. Set to 0 to disable.
export async function streamBulkStage({
  products,
  signal,
  onEvent,
  inactivityMs = DEFAULT_INACTIVITY_MS,
}) {
  // We chain the caller's external signal into our own internal controller
  // so the inactivity watchdog can abort the fetch without disturbing the
  // caller's signal (and the caller's abort still works as expected).
  const internalController = new AbortController();
  const onExternalAbort = () => internalController.abort();
  if (signal) {
    if (signal.aborted) internalController.abort();
    else signal.addEventListener("abort", onExternalAbort);
  }

  let inactivityTimer = null;
  let inactivityFired = false;
  const armInactivityTimer = () => {
    if (inactivityTimer) clearTimeout(inactivityTimer);
    if (inactivityMs > 0) {
      inactivityTimer = setTimeout(() => {
        inactivityFired = true;
        internalController.abort();
      }, inactivityMs);
    }
  };

  try {
    const response = await fetch(buildUrl(), {
      method: "POST",
      headers: buildHeaders(),
      body: JSON.stringify({ products }),
      signal: internalController.signal,
    });

    if (!response.ok) {
      // Request-level rejection — body is a single JSON object, not NDJSON.
      let body = null;
      try {
        body = await response.json();
      } catch (_) {
        // Non-JSON error body (e.g. nginx 502); fall through with body=null.
      }
      throw new BulkStageRequestError(response.status, body?.error);
    }

    if (!response.body) {
      throw new Error("Response has no body — streaming not supported in this environment");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";

    armInactivityTimer();

    // The split-pop pattern below protects against the chunk boundary landing
    // mid-line: split on \n, treat the last element as a fragment that may be
    // incomplete, and prepend it to the next chunk's decoded text.
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      armInactivityTimer();
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop();
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        let event;
        try {
          event = JSON.parse(trimmed);
        } catch (e) {
          console.warn("[bulkStageClient] non-JSON event line", trimmed);
          continue;
        }
        onEvent(event);
      }
    }
    // Flush any trailing complete event the server emitted without a newline.
    const tail = buffer.trim();
    if (tail) {
      try {
        onEvent(JSON.parse(tail));
      } catch (e) {
        console.warn("[bulkStageClient] non-JSON tail fragment", tail);
      }
    }
  } catch (err) {
    // If we aborted due to inactivity, surface a typed error so callers can
    // distinguish "user cancelled" (AbortError) from "server went silent".
    if (inactivityFired) {
      throw new BulkStageStreamTimeoutError(inactivityMs);
    }
    throw err;
  } finally {
    if (inactivityTimer) clearTimeout(inactivityTimer);
    if (signal) signal.removeEventListener("abort", onExternalAbort);
  }
}
